/**
 * Media Library Hook
 * 
 * Handles fetching and managing media library data
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@/lib/api-client';
import { getAuthToken } from '@/hooks/use-auth';

export interface MediaItem {
  id: string;
  filename: string;
  originalFilename: string;
  url: string;
  fileSize: number;
  fileType: 'image' | 'video' | 'audio' | 'document' | 'other';
  mimeType: string;
  width?: number | null;
  height?: number | null;
  altText?: string | null;
  description?: string | null;
  folderPath: string;
  uploadedAt: string;
  createdAt: string;
}

export interface MediaStats {
  totalFiles: number;
  images: number;
  videos: number;
  audio: number;
  documents: number;
  other: number;
  totalStorage: number;
  totalStorageFormatted: string;
}

export interface MediaListResponse {
  media: MediaItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface MediaListParams {
  page?: number;
  pageSize?: number;
  fileType?: string;
  search?: string;
  sortBy?: 'uploaded_at' | 'file_size' | 'filename';
  sortDir?: 'asc' | 'desc';
  folder?: string;
}

export function useMediaLibrary(params: MediaListParams = {}) {
  const apiClient = getApiClient();
  
  return useQuery<MediaListResponse>({
    queryKey: ['media', params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.fileType) queryParams.append('fileType', params.fileType);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDir) queryParams.append('sortDir', params.sortDir);
      if (params.folder !== undefined) queryParams.append('folder', params.folder);
      
      return apiClient.request<MediaListResponse>(`/admin/media?${queryParams.toString()}`);
    },
  });
}

export function useMediaStats() {
  const apiClient = getApiClient();
  
  return useQuery<MediaStats>({
    queryKey: ['media', 'stats'],
    queryFn: async () => {
      return apiClient.request<MediaStats>('/admin/media/stats');
    },
  });
}

export function useDeleteMedia() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (mediaId: string) => {
      return apiClient.request<{ success: boolean }>(`/admin/media/${mediaId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
}

export function useUploadMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, folder }: { file: File; folder?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (folder) formData.append('folder', folder);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Use standalone upload server on port 3005
      const response = await fetch(`http://localhost:3005/admin/media/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: formData,
      });
      
      // Clone response before reading to avoid "Body already used" error
      const responseClone = response.clone();
      
      if (!response.ok) {
        let errorMessage = `Upload failed with status ${response.status}`;
        try {
          const errorText = await responseClone.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.error || errorData.message || errorMessage;
            } catch {
              // If not JSON, use the text as error message
              errorMessage = errorText || errorMessage;
            }
          }
        } catch (e) {
          // If reading fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Parse response JSON
      try {
        const responseText = await response.text();
        if (!responseText) {
          return { success: true };
        }
        return JSON.parse(responseText);
      } catch (e) {
        // If response is not JSON, return success object
        return { success: true };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['media', 'stats'] });
    },
  });
}

export function useUpdateMedia() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, altText, description, folderPath }: { 
      id: string; 
      altText?: string; 
      description?: string; 
      folderPath?: string;
    }) => {
      return apiClient.request<{ media: MediaItem }>(`/admin/media/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ altText, description, folderPath }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
}

